import { useEffect } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import SchemaOrg, { buildArticleSchema } from "@/components/base/SchemaOrg";
import type { ArticleData } from "@/mocks/articles";

interface ArticleLayoutProps {
  article: ArticleData;
}

const SITE_URL = "https://hoobuy.tattoo";

export default function ArticleLayout({ article }: ArticleLayoutProps) {
  const articleUrl = `${SITE_URL}/${article.slug}`;
  const schemaData = buildArticleSchema({
    headline: article.title,
    description: article.description,
    image: article.featuredImage,
    datePublished: article.date,
    dateModified: article.date,
    url: articleUrl,
  });

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" as const });
  }, []);

  return (
    <>
      <SchemaOrg type="Article" data={schemaData as Record<string, unknown>} />
      <title>{article.title}</title>
      <meta name="description" content={article.description} />
      <meta name="keywords" content={article.keywords} />
      <link rel="canonical" href={articleUrl} />
      <meta property="og:title" content={article.title} />
      <meta property="og:description" content={article.description} />
      <meta property="og:url" content={articleUrl} />
      <meta property="og:type" content="article" />
      <meta property="og:image" content={article.featuredImage} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={article.title} />
      <meta name="twitter:description" content={article.description} />
      <meta name="twitter:image" content={article.featuredImage} />

      <Navbar />
      <main>
        <section className="relative w-full pt-28 pb-16 md:pt-32 md:pb-20 bg-background-50">
          <div className="w-full max-w-[900px] mx-auto px-4 md:px-6">
            <div className="flex items-center flex-wrap gap-2 mb-4">
              <Link
                to="/"
                className="text-xs font-medium text-accent-500 hover:text-accent-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                &larr; Voltar ao Início
              </Link>
              <span className="text-foreground-300 text-xs">/</span>
              <Link
                to="/hoobuyspreadsheet"
                className="text-xs text-foreground-400 hover:text-foreground-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                Guia de Compras
              </Link>
              <span className="text-foreground-300 text-xs">/</span>
              <span className="text-xs text-foreground-400 truncate max-w-[180px]">
                {article.title.length > 40 ? article.title.slice(0, 40) + "..." : article.title}
              </span>
            </div>
            <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-extrabold text-foreground-900 leading-tight">
              {article.title}
            </h1>
            <p className="mt-4 text-sm text-foreground-500">
              Publicado em {new Date(article.date).toLocaleDateString("pt-BR")} &bull; Atualizado recentemente
            </p>
          </div>
        </section>

        <section className="w-full bg-background-50 pb-10">
          <div className="w-full max-w-[900px] mx-auto px-4 md:px-6">
            <div className="w-full h-[300px] md:h-[400px] rounded-2xl overflow-hidden">
              <img
                src={article.featuredImage}
                alt={article.title}
                title={article.title}
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>
        </section>

        <article className="w-full pb-16 md:pb-24">
          <div className="w-full max-w-[760px] mx-auto px-4 md:px-6">
            <div className="text-foreground-800 leading-relaxed space-y-8">
              {article.sections.map((section, idx) => (
                <section key={idx}>
                  <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mt-10 mb-4">
                    {section.heading}
                  </h2>
                  {section.paragraphs.map((p, pIdx) => (
                    <p
                      key={pIdx}
                      className="text-foreground-700 leading-relaxed mb-4"
                      dangerouslySetInnerHTML={{ __html: p }}
                    />
                  ))}
                </section>
              ))}

              <div className="mt-12 pt-8 border-t border-background-200/70">
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 p-6 bg-background-50 rounded-xl">
                  <div>
                    <p className="text-sm font-semibold text-foreground-900">
                      Gostou deste artigo?
                    </p>
                    <p className="text-xs text-foreground-500 mt-1">
                      Volte para a página inicial e explore todas as categorias do hoobuy spreadsheet.
                    </p>
                  </div>
                  <Link
                    to="/"
                    className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-white bg-primary-500 hover:bg-primary-600 rounded-full transition-all cursor-pointer whitespace-nowrap"
                  >
                    Ver Categorias
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-arrow-right-line"></i>
                    </span>
                  </Link>
                </div>
              </div>

              <div className="mt-6 flex flex-col sm:flex-row gap-3 p-5 bg-accent-50 rounded-xl border border-accent-100">
                <div className="flex-1">
                  <p className="text-sm font-semibold text-accent-800">
                    Quer mais guias como este?
                  </p>
                  <p className="text-xs text-accent-600 mt-0.5">
                    Confira nosso guia completo de compras e todos os artigos disponíveis.
                  </p>
                </div>
                <Link
                  to="/hoobuyspreadsheet"
                  className="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-semibold text-accent-700 bg-white rounded-full border border-accent-200 hover:bg-accent-100 transition-colors cursor-pointer whitespace-nowrap"
                >
                  Guia de Compras
                  <span className="w-3.5 h-3.5 flex items-center justify-center">
                    <i className="ri-arrow-right-line"></i>
                  </span>
                </Link>
              </div>
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}